import snakeKeys from 'snakecase-keys';

import { isNotNullish } from '../common/types';
import { type OrionRequestAttributes, type SegmentationsToForce } from '../model/configuration';
import { type Experiment } from '../model/experiment';
import { type Fetcher } from '../model/fetcher';
import { type OrionSegmentations } from '../model/identityConfiguration';

export type APIManager = ReturnType<typeof createAPIManager>;

type OrionConfigurationResponse = {
  settings: Record<string, unknown>;
  segmentations: OrionSegmentations;
  settings_hash: string;
};

export type APIManagerConfig = {
  fetcher: Fetcher;
  appId: string;
  baseUrl: string;
  // Whether the API calls are sent to a proxy, or directly to Orion APIs
  goesThroughOrionProxy: boolean;
};

export class Error4xx extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'Error4xx';
  }
}

async function validateResponse(response: Response): Promise<unknown> {
  // identify 40x errors. We don't want to retry them.
  if (response.status >= 400 && response.status < 500) {
    throw new Error4xx(
      `Received invalid response! Code: ${response.status}, Message: ${response.statusText}, Source: proxy`,
    );
  }
  // if response 200 we rely on the fact it is a JSON answer. But if it is not, we cannot ask for json parse.
  // NOTE: it is important to avoid call .json multiple times, otherwise it will fail.
  let json: unknown = null;
  try {
    json = await response.json();
  } catch {
    throw new Error(
      `Received invalid response! Code: ${response.status}, Message: ${response.statusText}, Extra: Invalid JSON content, Source: proxy`,
    );
  }
  if (response.status !== 200) {
    // if for some reason we get empty value? Prevent error construction failure.
    const jsonErrorMessage: { code: unknown; message: unknown; extra: string } = (() => {
      if (typeof json !== 'object' || !json) {
        return { code: '', message: 'Received unexpected json', extra: JSON.stringify(json) };
      }
      // we rely on the fact that fields are of the right type (if available). Note we can always destructure, but it can return invalid values.
      // as is used only to suppress typescript error.
      const { error_code, code, error_message, message, extra } = json as Record<string, unknown>;
      return {
        code: error_code ?? code ?? '',
        message: error_message ?? message ?? '',
        extra: JSON.stringify(extra ?? {}),
      };
    })();
    throw new Error(
      `Received invalid response! Code: ${jsonErrorMessage.code}, Message: ${jsonErrorMessage.message}, Extra: ${jsonErrorMessage.extra}, Source: orion`,
    );
  }
  return json;
}

/**
 * API manager is responsible for making requests to the Orion REST API.
 */
export default function createAPIManager(config: APIManagerConfig) {
  async function sendProxyRequest(requestConfig: {
    url: string;
    body?: Record<string, unknown>;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  }) {
    const response = await config.fetcher(requestConfig.url, {
      method: requestConfig.method,
      ...(requestConfig.body && { body: JSON.stringify(snakeKeys(requestConfig.body)) }),
      headers: {
        ...(requestConfig.body && { 'Content-Type': 'application/json' }),
        'bsp-id': config.appId,
      },
    });
    return validateResponse(response);
  }

  // For calls made to the proxy we expect Orion endpoints to be exposed as
  //  - <proxy-url>/orion/<orion-endpoint> by a proxy
  //  - <orion-url>/<orion-endpoint> by Orion directly
  const orionBaseUrlFull = config.goesThroughOrionProxy
    ? `${config.baseUrl}/orion`
    : config.baseUrl;
  return {
    async requestSettingsAndSegmentations(
      requestAttributes: OrionRequestAttributes,
    ): Promise<OrionConfigurationResponse> {
      const body = {
        ...requestAttributes,
        bspId: config.appId,
      };
      const json = await sendProxyRequest({
        url: `${orionBaseUrlFull}/v3/identity/settings`,
        body,
        method: 'POST',
      });
      return json as OrionConfigurationResponse;
    },
    async getAllExperiments(requestAttributes: OrionRequestAttributes): Promise<Experiment[]> {
      const body = {
        ...requestAttributes,
        returnIncompatible: true,
      };
      const json = await sendProxyRequest({
        url: `${orionBaseUrlFull}/v3/secret/experiments`,
        body,
        method: 'POST',
      });
      return json as Experiment[];
    },
    async forceExperimentSegmentation(
      experimentName: string,
      segmentIndex: number | undefined,
      segmentation: {
        idName: string;
        idValue: string;
      },
    ): Promise<void> {
      const segmentIndexParam = isNotNullish(segmentIndex) ? `&segment_index=${segmentIndex}` : '';
      const url = `${orionBaseUrlFull}/v3/secret/forced-segmentation?segmentation_id_name=${segmentation.idName}&segmentation_id=${segmentation.idValue}&experiment_name=${experimentName}${segmentIndexParam}`;
      await sendProxyRequest({
        url,
        body: {},
        method: isNotNullish(segmentIndex) ? 'PUT' : 'DELETE',
      });
    },
    async forceExperimentSegmentations(segmentations: SegmentationsToForce): Promise<void> {
      await sendProxyRequest({
        url: `${orionBaseUrlFull}/v3/secret/forced-segmentations`,
        method: 'PUT',
        body: {
          segmentations_to_force: segmentations,
        },
      });
    },
    async setSegmentationState(
      excludeFromSegmentation: boolean,
      segmentationIdName: string,
    ): Promise<void> {
      await sendProxyRequest({
        url: `${orionBaseUrlFull}/v3/secret/segmentation-state`,
        method: 'PUT',
        body: {
          excludeFromSegmentation,
          segmentationIdName,
        },
      });
    },
  };
}
